import { Link, useLocation, useNavigate } from "react-router-dom";
import { localizedPath } from "../lib/locale";
import { Button } from "../components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Menu, X, ChevronDown, Languages } from "lucide-react";
import { useState } from "react";
import { SiMeta, SiGoogle, SiTiktok, SiSnapchat } from "react-icons/si";
import SkalaLogo from "./SkalaLogo";
import CampaignBar from "./CampaignBar";
import { useLanguage } from "../contexts/LanguageContext";
import { LANGUAGE_LABELS, SUPPORTED_LANGUAGES } from "../i18n/translations";

const BingMark = () => <span className="font-display font-bold text-base">b</span>;
const TaboolaMark = () => <span className="font-display font-bold text-[10px] tracking-[0.15em]">T</span>;
const OutbrainMark = () => <span className="font-display font-bold text-[10px] tracking-[0.15em]">O</span>;

const platformLinks = [
    { slug: "meta", name: "Meta", desc: "Facebook · Instagram · WhatsApp", Icon: SiMeta, accent: "#C9C3B6" },
    { slug: "google", name: "Google", desc: "Search · YouTube · PMax", Icon: SiGoogle, accent: "#fff" },
    { slug: "tiktok", name: "TikTok", desc: "Spark · In-Feed · TopView", Icon: SiTiktok, accent: "#fff" },
    { slug: "snapchat", name: "Snapchat", desc: "Snap Ads · AR Lenses", Icon: SiSnapchat, accent: "#FFFC00" },
    { slug: "bing", name: "Bing / Microsoft", desc: "Search · Audience Network", Icon: BingMark, accent: "#fff" },
    { slug: "taboola", name: "Taboola", desc: "Native on premium publishers", Icon: TaboolaMark, accent: "#fff" },
    { slug: "outbrain", name: "Outbrain", desc: "Native on top news sites", Icon: OutbrainMark, accent: "#fff" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { language, setLanguage, t } = useLanguage();
    const navigate = useNavigate();
    const { pathname: currentPath } = useLocation();
    const switchLanguage = (code) => {
        setLanguage(code);
        const target = localizedPath(code, currentPath);
        if (target !== currentPath) navigate(target);
    };
    const location = useLocation();
    const isHome = location.pathname === "/";

    const otherLinks = [
        { to: "/solutions", label: language === "tr" ? "Çözümler" : language === "ru" ? "Решения" : language === "es" ? "Soluciones" : language === "pt" ? "Soluções" : "Solutions" },
        { href: "#volume-rewards", label: t("navbar.volumeRewards", "Volume Rewards") },
        { href: "#pricing", label: t("navbar.pricing", "Pricing") },
        { to: "/blog", label: t("navbar.resources", "Resources") },
        { href: "#faq", label: t("navbar.faq", "FAQ") },
    ];

    return (
        <header data-testid="site-navbar" className="fixed top-0 inset-x-0 z-50">
            <CampaignBar />
            <div className="container-skala">
                <div className="mt-2 flex items-center justify-between h-14 md:h-16 px-4 md:px-5 rounded-2xl border border-white/10 bg-background/60 backdrop-blur-2xl shadow-[0_20px_60px_-30px_rgba(0, 0, 0,0.5)]">
                    <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-2">
                        <SkalaLogo />
                    </Link>

                    <nav className="hidden md:flex items-center gap-1">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button data-testid="navbar-platforms-trigger" className="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors outline-none">
                                    {t("navbar.platforms", "Platforms")}
                                    <ChevronDown className="w-3.5 h-3.5" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" sideOffset={12} data-testid="navbar-platforms-dropdown" className="w-[420px] p-2 bg-card/95 backdrop-blur-xl border-white/10">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-[60vh] overflow-auto pr-1" data-testid="platforms-dropdown-grid">
                                    {platformLinks.map((p) => (
                                        <DropdownMenuItem key={p.slug} asChild className="p-0 focus:bg-transparent">
                                            <Link to={`/platforms/${p.slug}`} data-testid={`navbar-platform-link-${p.slug}`} className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/[0.04] transition-colors w-full cursor-pointer">
                                                <div className="w-9 h-9 rounded-lg bg-white/[0.04] border border-white/10 grid place-items-center shrink-0" style={{ color: p.accent }}>
                                                    <p.Icon className="w-4 h-4" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-semibold leading-tight">{p.name}</p>
                                                    <p className="text-[10px] font-mono text-muted-foreground tracking-wide truncate mt-0.5">{p.desc}</p>
                                                </div>
                                            </Link>
                                        </DropdownMenuItem>
                                    ))}
                                </div>
                                <div className="mt-2 pt-2 border-t border-white/5">
                                    <DropdownMenuItem asChild className="p-0 focus:bg-transparent">
                                        <Link to="/#pricing" data-testid="navbar-platform-link-all-access" className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-white/[0.04] border border-white/10 bg-white/[0.02] transition-colors cursor-pointer">
                                            <div>
                                                <p className="text-sm font-semibold text-foreground">{t("navbar.allAccessTitle", "All platforms included")} — $0/mo</p>
                                                <p className="text-[10px] font-mono text-muted-foreground mt-0.5">{t("navbar.allAccessHint", "No subscription · volumetric top-up fee only")}</p>
                                            </div>
                                            <span className="text-highlight text-sm">→</span>
                                        </Link>
                                    </DropdownMenuItem>
                                </div>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {otherLinks.map((l) =>
                            l.to ? (
                                <Link key={l.label} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">{l.label}</Link>
                            ) : (
                                isHome && (
                                    <a key={l.label} href={l.href} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors">{l.label}</a>
                                )
                            ),
                        )}
                    </nav>

                    <div className="hidden md:flex items-center gap-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid="language-selector-trigger" className="text-muted-foreground hover:text-foreground hover:bg-white/5">
                                    <Languages className="w-4 h-4 mr-1.5" />
                                    {LANGUAGE_LABELS[language]}
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-card border-white/10">
                                {SUPPORTED_LANGUAGES.map((langCode) => (
                                    <DropdownMenuItem key={langCode} data-testid={`language-option-${langCode}`} onClick={() => switchLanguage(langCode)} className={`cursor-pointer ${language === langCode ? "text-highlight" : ""}`}>{LANGUAGE_LABELS[langCode]}</DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        <Link to="/login" data-testid="navbar-login-link">
                            <Button variant="ghost" size="sm" className="text-foreground hover:bg-white/5" data-testid="navbar-login-button">{t("navbar.signIn", "Sign in")}</Button>
                        </Link>
                        <Link to="/signup" data-testid="navbar-signup-link">
                            <Button data-testid="navbar-apply-button" className="btn-highlight rounded-xl h-9 px-4 glow-highlight">{t("navbar.apply", "Apply for Access")}</Button>
                        </Link>
                    </div>

                    <button aria-label="Toggle menu" data-testid="mobile-menu-toggle" className="md:hidden text-foreground" onClick={() => setOpen((v) => !v)}>
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div data-testid="mobile-menu" className="md:hidden container-skala">
                    <div className="mt-2 rounded-2xl border border-white/10 bg-background/95 backdrop-blur-xl p-4 flex flex-col gap-1">
                        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground pt-1 pb-1">{t("navbar.platforms", "Platforms")}</p>
                        {platformLinks.map((p) => (
                            <Link key={p.slug} to={`/platforms/${p.slug}`} onClick={() => setOpen(false)} data-testid={`mobile-platform-${p.slug}`} className="flex items-center gap-3 py-2 text-sm text-foreground/85 hover:text-foreground">
                                <p.Icon className="w-4 h-4" style={{ color: p.accent }} />
                                {p.name}
                            </Link>
                        ))}
                        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground pt-3 pb-1">{t("navbar.menu", "Menu")}</p>
                        {otherLinks.map((l) =>
                            l.to ? (
                                <Link key={l.label} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-base text-muted-foreground hover:text-foreground py-2">{l.label}</Link>
                            ) : (
                                isHome && (
                                    <a key={l.label} href={l.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-base text-muted-foreground hover:text-foreground py-2">{l.label}</a>
                                )
                            ),
                        )}
                        <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground pt-3 pb-1">{t("navbar.language", "Language")}</p>
                        <div className="grid grid-cols-2 gap-2 pb-2">
                            {SUPPORTED_LANGUAGES.map((langCode) => (
                                <button key={langCode} onClick={() => switchLanguage(langCode)} data-testid={`mobile-language-option-${langCode}`} className={`text-sm rounded-lg border px-3 py-2 ${language === langCode ? "border-highlight text-highlight bg-highlight/10" : "border-white/10 text-muted-foreground"}`}>{LANGUAGE_LABELS[langCode]}</button>
                            ))}
                        </div>
                        <div className="flex gap-2 pt-3">
                            <Link to="/login" className="flex-1" data-testid="mobile-login-link">
                                <Button variant="outline" className="w-full">{t("navbar.signIn", "Sign in")}</Button>
                            </Link>
                            <Link to="/signup" className="flex-1" data-testid="mobile-signup-link">
                                <Button className="w-full btn-highlight">{t("navbar.apply", "Apply")}</Button>
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
